import { Trophy, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtUSD } from './types';

/**
 * Live prize-pool calculator. Mirrors the design's PrizeCalculator:
 * - Total prize pool (header, large)
 * - Segmented bar showing bonus pool / survivor / playoff remainder
 * - 3 stat columns
 * - Inline warning if overdraft (bonuses > pool) or low playoff (<40% remains)
 *
 * `bonusPool` is the projected total = sum of (per-week bonus * weeksRemaining)
 * + sum of one-time / per-season bonuses. Caller computes; this component
 * just displays.
 */
export function PrizeCalculator({
  prizePool,
  bonusPool,
  survivorAmount,
  weeksRemaining,
}: {
  prizePool: number;
  bonusPool: number;
  survivorAmount: number;
  weeksRemaining: number;
}) {
  const totalReserved = bonusPool + survivorAmount;
  const remaining = prizePool - totalReserved;
  const remainingPct = prizePool > 0 ? Math.max(0, (remaining / prizePool) * 100) : 0;
  const overdraft = remaining < 0;
  const lowPlayoff = !overdraft && remainingPct < 40;
  const bonusPct = prizePool > 0 ? (bonusPool / prizePool) * 100 : 0;
  const survPct = prizePool > 0 ? (survivorAmount / prizePool) * 100 : 0;

  return (
    <div
      className={cn(
        'rounded-2xl p-5 sm:p-6 transition-colors',
        'bg-gradient-to-br from-slate-900 to-slate-950',
        overdraft
          ? 'border border-red-500/50 shadow-[0_0_30px_-10px_rgba(239,68,68,0.4)]'
          : lowPlayoff
            ? 'border border-amber-500/40'
            : 'border border-border',
      )}
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div
            className={cn(
              'text-[11px] font-bold uppercase tracking-[0.06em]',
              overdraft ? 'text-red-400' : 'text-muted-foreground',
            )}
          >
            {overdraft ? 'Over Budget' : 'Prize Pool Allocation'}
          </div>
          <div className="font-bold text-3xl mt-1 tabular-nums tracking-tight">
            {fmtUSD(prizePool)}
          </div>
        </div>
        <div className="w-12 h-12 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
          <Trophy className="w-5 h-5" />
        </div>
      </div>

      {/* Segmented bar */}
      <div className="h-3 rounded-full bg-muted overflow-hidden flex mb-4">
        {!overdraft && (
          <>
            <div
              className="bg-gradient-to-r from-orange-500 to-amber-500 transition-[width] duration-300"
              style={{ width: `${Math.min(100, bonusPct)}%` }}
            />
            <div
              className="bg-gradient-to-r from-red-500 to-orange-600 transition-[width] duration-300"
              style={{ width: `${Math.min(Math.max(0, 100 - bonusPct), survPct)}%` }}
            />
          </>
        )}
        {overdraft && (
          <div
            className="w-full"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg, hsl(0 84% 60%), hsl(0 84% 60%) 6px, hsl(0 84% 50%) 6px, hsl(0 84% 50%) 12px)',
            }}
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CalcStat
          label="Bonus Pool"
          sub={`projected over ${weeksRemaining} ${weeksRemaining === 1 ? 'week' : 'weeks'}`}
          value={fmtUSD(bonusPool)}
          dotClass="bg-orange-500"
        />
        <CalcStat
          label="Survivor"
          sub="one-time award"
          value={fmtUSD(survivorAmount)}
          dotClass="bg-red-500"
        />
        <CalcStat
          label={overdraft ? 'Overdraft' : 'Playoff Pool'}
          sub={overdraft ? 'reduce bonuses' : `${Math.round(remainingPct)}% of pool`}
          value={fmtUSD(remaining)}
          dotClass={overdraft ? 'bg-red-500' : 'bg-emerald-500'}
          negative={overdraft}
        />
      </div>

      {(overdraft || lowPlayoff) && (
        <div
          className={cn(
            'mt-4 px-4 py-3 rounded-lg text-sm flex gap-3 items-start',
            overdraft
              ? 'bg-red-500/8 border border-red-500/30 text-red-400'
              : 'bg-amber-500/8 border border-amber-500/30 text-amber-400',
          )}
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            {overdraft ? (
              <>
                <strong className="font-semibold">
                  Bonus pool exceeds prize pool by {fmtUSD(-remaining)}.
                </strong>{' '}
                Reduce bonus amounts or increase prize pool to save settings.
              </>
            ) : (
              <>
                <strong className="font-semibold">Heads up:</strong> Playoff pool is
                below 40% of total. Commissioner override available, but managers
                may grumble.
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CalcStat({
  label,
  sub,
  value,
  dotClass,
  negative,
}: {
  label: string;
  sub: string;
  value: string;
  dotClass: string;
  negative?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className={cn('w-2 h-2 rounded-full', dotClass)} />
        <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
          {label}
        </span>
      </div>
      <div
        className={cn(
          'font-bold text-2xl tabular-nums tracking-tight',
          negative ? 'text-red-400' : 'text-foreground',
        )}
      >
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}
